import { format, isValid, parseISO } from "date-fns";
import { getChartSeriesColor } from "@/constants/chartColors";
import type { GscTrendGranularity, GscTrendPoint } from "@/types/gsc";

export { getChartSeriesColor };

export type GscTrendMetric = "clicks" | "impressions";

export const GSC_TREND_METRICS = [
  "clicks",
  "impressions",
] as const satisfies readonly GscTrendMetric[];

export const isGscTrendMetric = (value: string): value is GscTrendMetric =>
  (GSC_TREND_METRICS as readonly string[]).includes(value);

export interface GscTrendChartSeries {
  key: string;
  domain: string;
  color: string;
}

export type GscTrendChartRow = {
  bucketDate: string;
  dateLabel: string;
} & Record<string, number | string | null>;

export const toSafeSeriesKey = (_domain: string, index: number): string =>
  `series_${index}`;

const parseBucketDate = (bucketDate: string): Date => {
  const normalized =
    bucketDate.length === 10 ? `${bucketDate}T00:00:00` : bucketDate;
  return parseISO(normalized);
};

export const formatGscTrendBucketLabel = (
  bucketDate: string,
  granularity: GscTrendGranularity,
): string => {
  const parsed = parseBucketDate(bucketDate);
  if (!isValid(parsed)) return bucketDate.slice(0, 10);
  if (granularity === "month") return format(parsed, "MMM yyyy");
  return format(parsed, "MMM d");
};

export const buildGscTrendChartModel = (
  points: GscTrendPoint[],
  granularity: GscTrendGranularity,
  selectedDomains: string[] | null,
  metric: GscTrendMetric = "clicks",
): { rows: GscTrendChartRow[]; series: GscTrendChartSeries[] } => {
  if (points.length === 0) {
    return { rows: [], series: [] };
  }

  const domainSet = new Set(points.map((point) => point.domain));
  const domains = (
    selectedDomains?.length
      ? selectedDomains.filter((domain) => domainSet.has(domain))
      : [...domainSet]
  ).sort();

  const series: GscTrendChartSeries[] = domains.map((domain, index) => ({
    key: toSafeSeriesKey(domain, index),
    domain,
    color: getChartSeriesColor(index),
  }));

  const seriesByDomain = new Map(
    series.map((item) => [item.domain, item] as const),
  );

  const valuesByBucketAndSeries = new Map<string, number>();
  for (const point of points) {
    const seriesItem = seriesByDomain.get(point.domain);
    if (!seriesItem) continue;
    valuesByBucketAndSeries.set(
      `${point.bucket_date}|${seriesItem.key}`,
      metric === "clicks" ? (point.clicks ?? 0) : (point.impressions ?? 0),
    );
  }

  const bucketDates = [...new Set(points.map((point) => point.bucket_date))].sort();

  const rows: GscTrendChartRow[] = bucketDates.map((bucketDate) => {
    const row: GscTrendChartRow = {
      bucketDate,
      dateLabel: formatGscTrendBucketLabel(bucketDate, granularity),
    };

    for (const item of series) {
      row[item.key] =
        valuesByBucketAndSeries.get(`${bucketDate}|${item.key}`) ?? 0;
    }

    return row;
  });

  return { rows, series };
};
